import React, { useState, useRef, useEffect, useMemo } from 'react';
import { X, Send, Download } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useAppState } from '../../hooks/useAppState';
import { useAuth } from '../../hooks/useAuth';
import { useSubscription } from '../../hooks/useSubscription';
import { askOlli, SUGGESTED_QUESTIONS, TIMEOUT_ERROR_CODE } from '../../services/aiService';
import OlliAvatar from './OlliAvatar';
import UpgradePrompt from '../common/UpgradePrompt';

const WELCOME_MESSAGE = "Ciao! Sono Olli, l'assistente AI di Forfy. Posso aiutarti con domande fiscali e gestione della tua P.IVA forfettaria.";

// Le risposte di Olli arrivano da un LLM: niente HTML grezzo (no rehype-raw),
// react-markdown by default ignora l'HTML inline, quindi nessuna superficie XSS.
const OLLI_MARKDOWN_COMPONENTS = {
  p: (props) => <p className="mb-2 last:mb-0" {...props} />,
  ul: (props) => <ul className="list-disc pl-4 mb-2 last:mb-0 space-y-0.5" {...props} />,
  ol: (props) => <ol className="list-decimal pl-4 mb-2 last:mb-0 space-y-0.5" {...props} />,
  li: (props) => <li className="leading-snug" {...props} />,
  strong: (props) => <strong className="font-semibold" {...props} />,
  em: (props) => <em className="italic" {...props} />,
  // eslint-disable-next-line jsx-a11y/anchor-has-content -- contenuto fornito da react-markdown via children
  a: (props) => <a className="underline break-words" target="_blank" rel="noopener noreferrer" {...props} />,
  code: (props) => <code className="px-1 py-0.5 rounded bg-black/10 font-mono text-[0.85em]" {...props} />,
  // eslint-disable-next-line jsx-a11y/heading-has-content -- contenuto fornito da react-markdown via children
  h1: (props) => <h1 className="font-semibold text-base mb-1" {...props} />,
  // eslint-disable-next-line jsx-a11y/heading-has-content -- contenuto fornito da react-markdown via children
  h2: (props) => <h2 className="font-semibold text-sm mb-1" {...props} />,
  // eslint-disable-next-line jsx-a11y/heading-has-content -- contenuto fornito da react-markdown via children
  h3: (props) => <h3 className="font-semibold text-sm mb-1" {...props} />,
};

export default function OlliChat() {
  const appState = useAppState();
  const {
    invoices, fiscalSettings, clients, user, darkMode, selectedYear,
    getFiscalDataForYear,
    createInvoice, updateInvoice, deleteInvoice,
    getNextSeqForYear, getInvoiceCodeForYear,
    createClient, timesheets, createTimesheet, updateTimesheet,
  } = appState;

  const { user: authUser } = useAuth();
  const { loading: subLoading, canAccessFeature } = useSubscription();
  const olliLocked = !subLoading && !canAccessFeature('olli');

  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showClientSuggestions, setShowClientSuggestions] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Funzioni app state da passare all'executor
  const appStateFunctions = useMemo(() => ({
    invoices, clients, timesheets, user, selectedYear,
    userId: authUser?.uid,
    createInvoice, updateInvoice, deleteInvoice,
    getNextSeqForYear, getInvoiceCodeForYear,
    createClient, createTimesheet, updateTimesheet,
  }), [invoices, clients, timesheets, user, selectedYear, authUser?.uid,
    createInvoice, updateInvoice, deleteInvoice,
    getNextSeqForYear, getInvoiceCodeForYear,
    createClient, createTimesheet, updateTimesheet]);

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, isLoading]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  // Blocca scroll body quando chat aperta su mobile
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Permette ad altri componenti (es. CTA nella dashboard) di aprire la chat
  // di Olli senza dover sollevare lo stato: window.dispatchEvent(new CustomEvent('olli:open'))
  useEffect(() => {
    const handleExternalOpen = () => setIsOpen(true);
    window.addEventListener('olli:open', handleExternalOpen);
    return () => window.removeEventListener('olli:open', handleExternalOpen);
  }, []);

  const sendMessage = async (text) => {
    const question = text.trim();
    if (!question || isLoading) return;

    setError(null);
    setMessages((prev) => [...prev, { role: 'user', content: question }]);
    setInput('');
    setIsLoading(true);

    try {
      const result = await askOlli(
        question,
        { invoices, fiscalSettings, clients, user, selectedYear, getFiscalDataForYear },
        appStateFunctions
      );
      const msg = { role: 'assistant', content: result.answer };
      if (result.toolResult?.data?.downloadUrl) {
        msg.downloadUrl = result.toolResult.data.downloadUrl;
        msg.fileName = result.toolResult.data.fileName;
      }
      setMessages((prev) => [...prev, msg]);
    } catch (err) {
      if (err.code === TIMEOUT_ERROR_CODE) {
        setError('Olli non ha risposto in tempo. Riprova manualmente.');
      } else if (err.code === 'functions/resource-exhausted') {
        setError(err.message);
      } else if (err.code === 'functions/unauthenticated') {
        setError('Devi essere loggato per usare Olli.');
      } else {
        setError('Si è verificato un errore. Riprova tra qualche istante.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setShowClientSuggestions(false);
    sendMessage(input);
  };

  const handleInputChange = (e) => {
    const val = e.target.value;
    setInput(val);

    // Mostra dropdown clienti quando l'utente digita #
    const hashIdx = val.lastIndexOf('#');
    if (hashIdx >= 0) {
      const query = val.slice(hashIdx + 1).toLowerCase();
      if (!query.includes(' ')) {
        setShowClientSuggestions(true);
      } else {
        setShowClientSuggestions(false);
      }
    } else {
      setShowClientSuggestions(false);
    }
  };

  // Filtra clienti per il dropdown #
  const filteredClients = useMemo(() => {
    if (!showClientSuggestions) return [];
    const hashIdx = input.lastIndexOf('#');
    if (hashIdx < 0) return [];
    const query = input.slice(hashIdx + 1).toLowerCase();
    return clients
      .filter((c) => c.name && c.name.toLowerCase().includes(query))
      .slice(0, 8);
  }, [showClientSuggestions, input, clients]);

  const showSuggestions = messages.length === 0;

  return (
    <>
      {/* Floating button */}
      {!isOpen && (
        <button
          data-testid="olli-trigger"
          onClick={() => setIsOpen(true)}
          className={`fixed bottom-[88px] right-4 md:bottom-6 md:right-6 z-40 flex items-center gap-1.5 px-3 py-2 rounded-full shadow-lg transition-all hover:scale-105 active:scale-95 ${
            darkMode
              ? 'bg-neutral-800 border border-neutral-700'
              : 'bg-white border border-neutral-200'
          }`}
          aria-label="Apri chat con Olli, assistente AI"
        >
          <OlliAvatar size="sm" />
          <span className={`text-sm font-medium hidden sm:inline ${darkMode ? 'text-white' : 'text-neutral-900'}`}>
            Olli
          </span>
          <span
            className="hidden sm:inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold uppercase bg-forfy-100 text-forfy-600"
            style={{ fontFamily: "'DM Sans', sans-serif", letterSpacing: '0.5px' }}
          >
            AI
          </span>
        </button>
      )}

      {/* Chat drawer */}
      {isOpen && (
        <div
          className={`fixed z-50 flex flex-col inset-0 w-full h-[100dvh] overscroll-none md:inset-auto md:bottom-6 md:right-6 md:w-[400px] md:h-auto md:max-h-[600px] md:rounded-2xl shadow-2xl border overflow-hidden ${
            darkMode
              ? 'bg-neutral-900 border-neutral-700'
              : 'bg-white border-neutral-200'
          }`}
        >
          {/* Header */}
          <div
            className={`flex items-center gap-3 px-4 py-3 border-b shrink-0 ${
              darkMode ? 'border-neutral-700' : 'border-neutral-200'
            }`}
          >
            <OlliAvatar size="sm" />
            <div className="flex-1 min-w-0">
              <p className={`text-sm font-semibold flex items-center gap-1.5 ${darkMode ? 'text-white' : 'text-neutral-900'}`}>
                Olli
                <span
                  className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold uppercase bg-forfy-100 text-forfy-600"
                  style={{ fontFamily: "'DM Sans', sans-serif", letterSpacing: '0.5px' }}
                >
                  AI
                </span>
              </p>
              <p className={`text-xs ${darkMode ? 'text-neutral-400' : 'text-neutral-500'}`}>
                Assistente Fiscale
              </p>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              className={`p-1.5 rounded-lg transition-colors ${
                darkMode
                  ? 'hover:bg-neutral-800 text-neutral-400'
                  : 'hover:bg-neutral-100 text-neutral-500'
              }`}
              aria-label="Chiudi chat"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Contenuto: bloccato per piano Free (post-trial), altrimenti chat normale */}
          {olliLocked ? (
            <div className="flex-1 overflow-y-auto">
              <UpgradePrompt
                title="Olli AI"
                message="Sblocca l'assistente fiscale Olli con il piano Pro."
              />
            </div>
          ) : (
          <>
          {/* Messages body */}
          <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
            {/* Welcome message */}
            <div className="flex items-start gap-2">
              <OlliAvatar size="sm" />
              <div
                className={`rounded-2xl rounded-tl-md px-3 py-2 max-w-[80%] text-sm ${
                  darkMode
                    ? 'bg-neutral-800 text-neutral-200'
                    : 'bg-neutral-100 text-neutral-800'
                }`}
              >
                {WELCOME_MESSAGE}
              </div>
            </div>

            {/* Suggested questions */}
            {showSuggestions && (
              <div className="flex flex-wrap gap-2 pl-10">
                {SUGGESTED_QUESTIONS.map((q, i) => (
                  <button
                    key={i}
                    onClick={() => sendMessage(q)}
                    className={`text-xs px-3 py-1.5 rounded-full border transition-colors text-left ${
                      darkMode
                        ? 'border-neutral-700 text-neutral-300 hover:bg-neutral-800'
                        : 'border-neutral-300 text-neutral-600 hover:bg-neutral-50'
                    }`}
                  >
                    {q}
                  </button>
                ))}
              </div>
            )}

            {/* Chat messages */}
            {messages.map((msg, i) => (
              <div
                key={i}
                className={`flex items-start gap-2 ${msg.role === 'user' ? 'justify-end' : ''}`}
              >
                {msg.role === 'assistant' && <OlliAvatar size="sm" />}
                <div
                  data-testid={msg.role === 'user' ? 'olli-message-user' : 'olli-message-assistant'}
                  className={`rounded-2xl px-3 py-2 max-w-[80%] text-sm ${
                    msg.role === 'user' ? 'whitespace-pre-wrap' : 'break-words'
                  } ${
                    msg.role === 'user'
                      ? 'bg-forfy-400 text-white rounded-tr-md'
                      : darkMode
                        ? 'bg-neutral-800 text-neutral-200 rounded-tl-md'
                        : 'bg-neutral-100 text-neutral-800 rounded-tl-md'
                  }`}
                >
                  {msg.role === 'assistant' ? (
                    <ReactMarkdown remarkPlugins={[remarkGfm]} components={OLLI_MARKDOWN_COMPONENTS}>
                      {msg.content}
                    </ReactMarkdown>
                  ) : (
                    msg.content
                  )}
                  {msg.downloadUrl && (
                    <a
                      href={msg.downloadUrl}
                      download={msg.fileName || 'export.csv'}
                      className={`inline-flex items-center gap-1 mt-2 px-2 py-1 rounded-lg text-xs font-medium transition-colors ${
                        darkMode
                          ? 'bg-neutral-700 hover:bg-neutral-600 text-neutral-200'
                          : 'bg-white hover:bg-neutral-50 text-neutral-700 border border-neutral-200'
                      }`}
                    >
                      <Download className="w-3 h-3" />
                      Scarica CSV
                    </a>
                  )}
                </div>
              </div>
            ))}

            {/* Loading indicator */}
            {isLoading && (
              <div className="flex items-start gap-2">
                <OlliAvatar size="sm" />
                <div
                  data-testid="olli-loading"
                  className={`rounded-2xl rounded-tl-md px-3 py-2 text-sm ${
                    darkMode
                      ? 'bg-neutral-800 text-neutral-400'
                      : 'bg-neutral-100 text-neutral-500'
                  }`}
                >
                  <span className="inline-flex items-center gap-1">
                    Olli sta pensando
                    <span className="olli-dots">
                      <span className="olli-dot" />
                      <span className="olli-dot" />
                      <span className="olli-dot" />
                    </span>
                  </span>
                </div>
              </div>
            )}

            {/* Error message */}
            {error && (
              <div
                className={`rounded-xl px-3 py-2 text-sm ${
                  darkMode
                    ? 'bg-red-900/30 text-red-300 border border-red-800'
                    : 'bg-red-50 text-red-700 border border-red-200'
                }`}
              >
                {error}
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Input footer */}
          <form
            onSubmit={handleSubmit}
            className={`relative flex items-center gap-2 px-4 py-3 border-t shrink-0 ${
              darkMode ? 'border-neutral-700' : 'border-neutral-200'
            }`}
          >
            {/* Dropdown #cliente */}
            {showClientSuggestions && filteredClients.length > 0 && (
              <div
                className={`absolute bottom-full left-4 right-4 mb-1 max-h-40 overflow-y-auto rounded-xl border shadow-lg ${
                  darkMode
                    ? 'bg-neutral-800 border-neutral-700'
                    : 'bg-white border-neutral-200'
                }`}
              >
                {filteredClients.map((c) => (
                  <button
                    key={c.id || c.name}
                    type="button"
                    onClick={() => {
                      // Sostituisci #query con il nome del cliente
                      const hashIdx = input.lastIndexOf('#');
                      setInput(input.slice(0, hashIdx) + c.name + ' ');
                      setShowClientSuggestions(false);
                      inputRef.current?.focus();
                    }}
                    className={`w-full text-left text-sm px-3 py-2 transition-colors ${
                      darkMode
                        ? 'hover:bg-neutral-700 text-neutral-200'
                        : 'hover:bg-neutral-50 text-neutral-800'
                    }`}
                  >
                    {c.name}
                  </button>
                ))}
              </div>
            )}
            <input
              data-testid="olli-input"
              ref={inputRef}
              type="text"
              value={input}
              onChange={handleInputChange}
              placeholder="Scrivi un messaggio..."
              disabled={isLoading}
              className={`flex-1 text-base md:text-sm px-3 py-2 rounded-xl border outline-none transition-colors ${
                darkMode
                  ? 'bg-neutral-800 border-neutral-700 text-white placeholder:text-neutral-500 focus:border-neutral-500'
                  : 'bg-neutral-50 border-neutral-200 text-neutral-900 placeholder:text-neutral-400 focus:border-neutral-400'
              }`}
            />
            <button
              data-testid="olli-submit"
              type="submit"
              disabled={!input.trim() || isLoading}
              className={`p-2 rounded-xl transition-colors ${
                input.trim() && !isLoading
                  ? 'bg-forfy-400 text-white hover:bg-forfy-500'
                  : darkMode
                    ? 'bg-neutral-800 text-neutral-600'
                    : 'bg-neutral-100 text-neutral-400'
              }`}
              aria-label="Invia messaggio"
            >
              <Send className="w-4 h-4" />
            </button>
          </form>
          </>
          )}
        </div>
      )}

      {/* CSS for animated dots */}
      <style>{`
        .olli-dots {
          display: inline-flex;
          gap: 2px;
          margin-left: 2px;
        }
        .olli-dot {
          width: 4px;
          height: 4px;
          border-radius: 50%;
          background: currentColor;
          animation: olliBounce 1.4s ease-in-out infinite;
        }
        .olli-dot:nth-child(2) { animation-delay: 0.2s; }
        .olli-dot:nth-child(3) { animation-delay: 0.4s; }
        @keyframes olliBounce {
          0%, 80%, 100% { opacity: 0.3; transform: translateY(0); }
          40% { opacity: 1; transform: translateY(-3px); }
        }
      `}</style>
    </>
  );
}
